import { Component, ContentChildren, forwardRef, Inject, Input, OnInit, QueryList } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GET_DATA_ERROR, Permissions } from '../../common/constants';
import { DynamicListColumn } from '../../dashboard-area/dynamic-list/dynamic-list-column';
import { DynamicListRow } from '../../dashboard-area/dynamic-list/dynamic-list-row';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { BreadcrumbService } from '../../service/breadcrumb.service';
import { CustomPropertyType } from '../../service/custom-property.service';
import { NavigationService } from '../../service/navigation.service';
import { CapitalizePipe, DatetimeFormatterPipe } from '../../shared/pipe/index';
import { COMPONENT_DEFINITION_REF } from "../../shared/utility/component-definition-token";
import { CustomerListWidgetService } from './customer-list-widget.service';

@Component({
    selector: 'customer-list-widget',
    template: require('./customer-list-widget.component.html'),
    providers: [CustomerListWidgetService]
})
export class CustomerListWidgetComponent implements OnInit {

    @Input() enableActions = true;

    @Input() title: string;

    @ContentChildren(COMPONENT_DEFINITION_REF) private columnComponents: QueryList<any>;

    loaded: boolean;
    error: string;
    tableColumns: DynamicListColumn[];
    tableData: DynamicListRow[];
    mobile: boolean;
    writePermission: boolean;
    readPermission: boolean;
    printTitle$: Observable<boolean>;

    private defaultColumns: { [key: string]: DynamicListColumn } = {
        name: { name: 'name', label: Promise.resolve('customerProperty'), pipe: null, sorting: null, visible: true, isMetric: false, path: 'name' },
        code: { name: 'code', label: Promise.resolve('customerCodeProperty'), pipe: null, sorting: null, visible: true, isMetric: false, path: 'code' },
        type: { name: 'type', label: Promise.resolve('customerTypeProperty'), pipe: CapitalizePipe, sorting: null, visible: true, isMetric: false, path: 'type' },
        country: { name: 'country', label: Promise.resolve('countryProperty'), pipe: null, sorting: null, visible: true, isMetric: false, path: 'country' },
        serviceLevel: { name: 'serviceLevel', label: Promise.resolve('serviceLevelProperty'), pipe: 'defaultServiceLevel', sorting: 'service-level-sorting', visible: this.getColumnVisibility('serviceLevel'), isMetric: false, path: 'subscription/serviceLevel' },
        firstAccessTimestamp: { name: 'firstAccessTimestamp', label: Promise.resolve('firstAccessProperty'), pipe: DatetimeFormatterPipe, sorting: null, visible: true, isMetric: false, path: 'firstAccessTimestamp' },
        lastAccessTimestamp: { name: 'lastAccessTimestamp', label: Promise.resolve('lastAccessProperty'), pipe: DatetimeFormatterPipe, sorting: null, visible: true, isMetric: false, path: 'lastAccessTimestamp' }
    };

    constructor(
        @Inject(forwardRef(() => BreadcrumbService)) private breadcrumbService: BreadcrumbService,
        @Inject(forwardRef(() => CustomerListWidgetService)) private customerListService: CustomerListWidgetService,
        @Inject(forwardRef(() => NavigationService)) private navigationService: NavigationService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) private appService: AppService
    ) { }

    getDefaultColumns(): { [key: string]: DynamicListColumn } {
        return this.defaultColumns;
    }

    getDefaultColumnNames(): string[] {
        return Object.keys(this.getDefaultColumns());
    }

    ngAfterContentInit() {
        this.tableColumns = this.customerListService.getTableColumns(this.columnComponents, this.getDefaultColumns(), ['name', 'code', 'type', 'country', 'serviceLevel'], CustomPropertyType.Customer);
        this.customerListService.init(this.columnComponents, this.getDefaultColumnNames())
            .then(() => {
                this.tableData = this.customerListService.getData();
            })
            .then(() => this.loaded = true)
            .catch(() => {
                this.loaded = true;
                this.error = GET_DATA_ERROR;
            });
    }

    ngOnDestroy() {
        this.customerListService.dispose();
    }

    ngOnInit() {
        this.readPermission = this.authenticationService.hasPermission(Permissions.READ_CUSTOMER);
        this.loaded = false;
        this.mobile = this.appService.isMobile();
        this.printTitle$ = this.breadcrumbService.get().pipe(map(tokens => tokens.map(token => token.name).indexOf('allThings') < 0));
        this.writePermission = this.authenticationService.hasPermission(Permissions.WRITE_CUSTOMER);
    }

    goToCustomerDetails(rowIndex: number): void {
        const cell = this.tableData[rowIndex];
        const link = ['/dashboard/customer_details', cell.id];
        this.navigationService.navigateTo(link);
    }

    getColumnVisibility(columnName: string): boolean {
        if (columnName == 'serviceLevel') {
            return this.authenticationService.getTenant().serviceLevelsEnabled && (this.authenticationService.hasPermission(Permissions.READ_SUBSCRIPTION) || this.authenticationService.hasPermission(Permissions.WRITE_SUBSCRIPTION));
        } else {
            return true;
        }
    }

    addCustomer(): void {
        this.navigationService.navigateTo(['/dashboard/customer_details/add']);
    }
}